import React from 'react';
import { FileText, AlertTriangle, FileCheck, Beaker } from 'lucide-react';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';

export const UserGuideAppNotes: React.FC = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    return (
        <section id="app-notes">
            <h2 className={clsx('text-2xl font-bold mb-6 flex items-center gap-3', isDark ? 'text-white' : 'text-slate-800')}>
                <FileText className="text-amber-500" />
                Application Notes
            </h2>

            <div className="grid grid-cols-1 gap-6">
                <div id="note-fracas" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <h3 className={clsx('text-lg font-bold mb-2 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                        <AlertTriangle className="text-yellow-500" size={20} />
                        Effective FRACAS Implementation
                    </h3>
                    <p className="text-slate-500 mb-4 text-sm">
                        A successful Failure Reporting, Analysis, and Corrective Action System (FRACAS) requires discipline and closed-loop tracking.
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Report All Incidents:</strong> Capture data from field, manufacturing, and lab testing.</li>
                        <li><strong>Categorize Accurately:</strong> Use consistent failure codes for meaningful analytics.</li>
                        <li><strong>Prioritize by Risk:</strong> Use Severity x Occurrence to focus on critical issues.</li>
                        <li><strong>Enforce Closure:</strong> Do not close an incident until the corrective action is verified.</li>
                    </ul>
                </div>

                <div id="note-8d" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <h3 className={clsx('text-lg font-bold mb-2 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                        <FileCheck className="text-green-500" size={20} />
                        Mastering 8D Problem Solving
                    </h3>
                    <p className="text-slate-500 mb-4 text-sm">
                        The 8 Disciplines (8D) methodology is robust but often misused. Avoid "jumping to solutions."
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>D2 (Problem Description):</strong> Describe the problem in quantifiable terms (Who, What, Where, When, How Many).</li>
                        <li><strong>D4 (Root Cause):</strong> Use 5-Why or Ishikawa to find the technical root cause AND the systemic root cause.</li>
                        <li><strong>D7 (Prevention):</strong> Update FMEAs and Control Plans to prevent recurrence systematically.</li>
                    </ul>
                </div>
                <div id="note-lab" className={clsx('p-6 rounded-xl border', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <h3 className={clsx('text-lg font-bold mb-2 flex items-center gap-2', isDark ? 'text-white' : 'text-slate-800')}>
                        <Beaker className="text-purple-500" size={20} />
                        Lab Integration Strategy
                    </h3>
                    <p className="text-slate-500 mb-4 text-sm">
                        Connecting lab data to FRACAS incidents creates a complete failure history record.
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <li><strong>Sample Tracking:</strong> Assign unique IDs to every failed part returned for analysis.</li>
                        <li><strong>Test Requests:</strong> Standardize test protocols for common failure modes.</li>
                        <li><strong>Evidence:</strong> Attach microscope images and test logs directly to the FRACAS record.</li>
                    </ul>
                </div>
            </div>
        </section>
    );
};
